/* @flow */
import * as React from 'react';
import to from 'to-case';
import { autorun } from 'mobx';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';

import ActionDrawerStore from 'stores/ActionDrawerStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { actions } from '../../actions';

import styles from './ActionDrawerWrapper.module.scss';

export type ActionDrawerWrapperProps = {
  children: Array<*>,
  appendActionToURL?: Boolean,
  closeBackdropOnClick?: Boolean,
  onActionCompleted?: Function,
};

type InjectedProps = {
  actionDrawerStore: ActionDrawerStore,
};

@inject('actionDrawerStore')
@withRouter
@observer
export default class ActionDrawerWrapper extends ComponentWithInjectedProps<
  ActionDrawerWrapperProps,
  {},
  InjectedProps
> {
  constructor(props: ActionDrawerWrapperProps & InjectedProps) {
    super(props);

    const { actionDrawerStore, onActionCompleted } = props;

    autorun(() => {
      if (actionDrawerStore.URLConfig.goToActionTypeRoute) {
        actionDrawerStore.closeActionDrawer();
      }

      if (actionDrawerStore.actionDrawerOpen && onActionCompleted) {
        actionDrawerStore.currentActionCallback =
          actionDrawerStore.currentActionCallback || onActionCompleted;
      }
    });
  }

  componentDidMount() {
    const { actionType } = this.props.match.params;
    const { actionDrawerStore, appendActionToURL } = this.props;

    if (appendActionToURL) {
      if (actionType && this.isActionValid(actionType)) {
        actionDrawerStore.openActionDrawer(to.camel(actionType));
        actionDrawerStore.URLConfig = {
          goToNormalRoute: this.goToNormalRoute.bind(this),
          routeWithoutActionType: this.removeActionTypeFromRoute(),
        };
      } else if (!actionType) {
        actionDrawerStore.URLConfig = {
          goToActionTypeRoute: this.goToActionTypeRoute.bind(this),
        };
      }
    }
  }

  componentWillUnmount() {
    const { actionDrawerStore } = this.props;

    actionDrawerStore.URLConfig = {
      routeWithoutActionType: '',
      goToNormalRoute: null,
      goToActionTypeRoute: null,
    };
  }

  isActionValid(routeActionParam: string) {
    return (
      actions.filter(item => {
        const actionListAction = to.pascal(item.action);
        const routeAction = to.pascal(routeActionParam);
        if (actionListAction === routeAction) return true;
      }).length > 0
    );
  }

  removeActionTypeFromRoute() {
    const { match } = this.props;
    const { path } = match;
    const { id } = match.params;
    const newPath = path.replace('/:actionType', '');

    return newPath.replace(':id', id);
  }

  goToNormalRoute() {
    const { history, actionDrawerStore, location } = this.props;
    const { actionType } = this.props.match.params;

    if (actionType)
      history.push(actionDrawerStore.URLConfig.routeWithoutActionType, {
        ...actionDrawerStore.currentActionDrawerData,
        ...location.state,
      });

    actionDrawerStore.actionDrawerOpen = false;
  }

  goToActionTypeRoute() {
    const { history, actionDrawerStore, location } = this.props;
    const route = history.location.pathname;
    const action = to.snake(actionDrawerStore.currentActionDrawer).replace(/_/g, '-');

    if (!route.includes(action))
      history.push(`${route}/${action}`, {
        ...actionDrawerStore.currentActionDrawerData,
        ...location.state,
      });

    actionDrawerStore.actionDrawerOpen = true;
  }

  render() {
    const { children, actionDrawerStore, closeBackdropOnClick = true } = this.props;

    return (
      <Drawer
        open={actionDrawerStore.actionDrawerOpen}
        elevation={0}
        anchor="right"
        onClose={() => closeBackdropOnClick && actionDrawerStore.closeActionDrawer()}
        ModalProps={{
          className: styles['action-drawer-modal'],
          BackdropProps: { className: styles.backdrop },
        }}
        PaperProps={{ className: styles.wrapper }}
      >
        {children}
      </Drawer>
    );
  }
}
